import { USER_ROLES } from "constants";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { displayNotification, generateOptionsFromData, sendAPIRequest } from "helpers";
import SiteLoader from "pages/components/SiteLoader";
import React, { useEffect, useMemo, useState, useRef, useContext } from "react";
import { useTranslation } from "react-i18next";
import Crumbs from "components/crumbs/Crumbs";
import MissionManagerModal from "modules/pvs_activities/components/Modal/MissionManagerModal";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Tooltip } from "@mui/material";
import MissionInfo from "modules/pvs_activities/components/mission/MissionInfo";
import MissionHeader from "modules/pvs_activities/components/mission/MissionHeader";
import {
  MissionContext,
} from "modules/pvs_activities/components/contexts/MissionTasksContext";
import MissionAbortModal from "./MissionAbortModal";
import MissionLeaveModal from "./MissionLeaveModal";
import MissionDashboard from "./MissionDashboard";
import MissionConfigurationPage from "./MissionConfigurationPage";
import MissionChangeTabs from "./MissionChangeTabs";
import EmailNotificationLogs from "modules/pvs_activities/components/EmailNotificationLogs";
import { MISSION_STATUS_LIST, MODULE_PREFIX } from "modules/pvs_activities/constants";
import { useMissionArchiveMutation } from "modules/pvs_activities/redux/service/missions";

const MissionDetail = ({ config, page = "create" }) => {
  const missionContext = useContext(MissionContext);
  const [isLoading, setIsLoading] = useState(false);
  const [userData, setUserData] = useState([]);
  const [isShow1, setIsShow1] = useState(false);
  const [isDelegate, setIsDelegate] = useState(false);
  const authData = useSelector((s) => s.auth.data);
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const initModal1 = () => setIsShow1(!isShow1);
  const [showAbortMission, setShowAbortMission] = useState(false);
  const [showLeaveMission, setShowLeaveMission] = useState(false);
  const [missionArchived, { isLoading: archivedLoading, isError }] = useMissionArchiveMutation()
  const crumbs = [
    {
      path: "/portal/",
      label: t("-1", "Home"),
      active: false,
    },
    {
      path: "/portal/mission-cc",
      label: t("-1", "Mission Control Center"),
      active: false,
    },
    {
      label: t("-1", "Mission WorkSpace"),
      active: page === "mission-logs" ? false : true,
    },
    ...(page === "mission-logs"
      ? [
        {
          label: t("-1", "Logs"),
          active: true,
        },
      ]
      : []),
  ];

  const userOptions = useMemo(() => {
    return generateOptionsFromData(userData, {
      value: "id",
      label: "name",
    });
  }, [userData]);

  const loadUserData = async () => {
    try {
      const ret = await sendAPIRequest(`/api/users/?role_id=3`);
      const res = await ret.json();
      if (res.success && res.status === 200) {
        setUserData(res.data);
      }
    } catch (err) {
      console.log({ err });
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    loadUserData();
  }, []);

  useEffect(() => {
    // Check if the current user is a delegate
    if (USER_ROLES.delegate.includes(parseInt(authData?.role_id))) {
      setIsDelegate(true);
    }
  }, [authData]);

  // Handle Signing Success Redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const movementId = params.get('movementId');
    const documentId = params.get('documentId');
    const reportId = params.get('reportId');
    const folderId = params.get('folderId');
    const fileName = params.get('fileName');
    const event = params.get('event');

    if (event === 'signing_success' && folderId && movementId && documentId && reportId && fileName) {
      handleSignedDocument(folderId, movementId, documentId, reportId, fileName);
    }
  }, [window.location.search]);

  const handleSignedDocument = async (folderId, movementId, documentId, reportId, fileName) => {
    try {
      const response = await sendAPIRequest(`/api/pvsa/letter-pdf-router/handle-signed?folderId=${folderId}&movementId=${movementId}&documentId=${documentId}&reportId=${reportId}&fileName=${fileName}`);

      if (!response.ok) {
        throw new Error('Failed to signed document');
      }

      displayNotification("success", "Document signed successfully");
    } catch (error) {
      console.error('Error handling signed document:', error);
      displayNotification("error", "Failed to signed document");
    }
  };

  const handleMissionInfo = () => {
    navigate(`/portal/mission-request/${id}/view/?return=/portal/mission-cc`);
  };

  const handleMissionConfigurartion = () => {
    navigate(`/portal/mission-process/${id}/configuration`);
  };

  const abortMission = (reason) => {
    if (canAbortMission()) {
      if (
        !window.confirm(t("-1", "Are you sure you want to abort this mission?"))
      )
        return;
      missionContext.abortMission(reason);
      setShowAbortMission(false);
    }
  };

  const canAccessMissionConfigurations = () => {
    if (
      USER_ROLES.business_admin.includes(parseInt(authData?.role_id)) ||
      USER_ROLES.it_admin.includes(parseInt(authData?.role_id)) ||
      USER_ROLES.delegate.includes(parseInt(authData?.role_id))
    ) {
      return true;
    }
    if (missionContext.missionDelegate === parseInt(authData?.id)) {
      return true;
    }
    return false;
  };

  const canArchiveMission = () => {
    if (
      USER_ROLES.business_admin.includes(parseInt(authData?.role_id)) ||
      USER_ROLES.it_admin.includes(parseInt(authData?.role_id)) ||
      missionContext.missionManager === parseInt(authData?.id)
    ) {
      return true;
    }
    return false;
  }

  const canAbortMission = () => {
    if (
      USER_ROLES.business_admin.includes(parseInt(authData?.role_id)) ||
      USER_ROLES.it_admin.includes(parseInt(authData?.role_id)) ||
      missionContext.missionManager === parseInt(authData?.id)
    ) {
      return true;
    }
    return false;
  };

  const handleArchiveMission = async () => {
    if (!window.confirm('Are you sure to archive this missions?')) return
    try {
      missionArchived({ missionId: id }).then((response) => {
        if (response.error) {
          displayNotification("error", "Failed to archive mission");
        } else {
          displayNotification("success", "Successfully mission Archived");
          navigate('/portal/mission-cc');
        }
      });
    } catch (error) {
      console.log({ error });
      displayNotification('error', 'failed to archived mission [B]')
    }

  };

  config = {
    ...config,
    showCanAbortMission: canAbortMission(),
    showCanConfigureMission: canAccessMissionConfigurations(),
    showCanArchiveMission: canArchiveMission()
  };

  return (
    <>
      {isLoading ? (
        <div className="card-body p-4">
          <SiteLoader position="relative" background="transparent" />
        </div>
      ) : (
        <div className="container my-2">
          <Crumbs crumbs={crumbs} />
          <MissionHeader />
          <div className="header card-woah border border-secondary d-flex px-4 py-2 shadow w-100 my-2">
            <div className="card-title1 lh-1">
              <div className="card-title1 lh-1">
                {page === "create"
                  ? t("-1", "Mission Info ")
                  : page === "configuration"
                    ? t("-1", "Mission Configuration")
                    : page === "changeform"
                      ? t("-1", "Mission Change Form")
                      : page === "mission-logs"
                        ? t("-1", "Email/Notifications Logs")
                        : null}

                {(USER_ROLES.business_admin.includes(parseInt(authData?.role_id)) ||
                  USER_ROLES.cbd_staff.includes(parseInt(authData?.role_id))
                ) && page !== "mission-logs" && (
                    <Tooltip
                      title="View Mission Request Information"
                      placement="top"
                      arrow
                    >
                      <OpenInNewIcon
                        className="text-primary"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          handleMissionInfo();
                        }}
                      />
                    </Tooltip>
                  )}
              </div>
            </div>
            <div className="header-right-items d-flex ms-auto">
              <div className="ms-auto">
                {page === "create" && (
                  <button
                    onClick={() => navigate("/portal/mission-cc")}
                    className="btn btn--outline fs-7 lh-1 ms-2"
                  >
                    <i className="fas fa-angle-left text-dark" />
                    {t("32", "Back")}
                  </button>
                )}

                {USER_ROLES.experts.includes(parseInt(authData?.role_id)) && (
                  <button
                    onClick={() => setShowLeaveMission(true)}
                    className="btn btn--outline fs-7 lh-1 ms-2"
                  >
                    {t("-1", "Leave Mission")}
                  </button>
                )}
              </div>
              {page === "create" && (
                <>
                  {" "}
                  {config.showCanConfigureMission && (
                    <div className="ms-auto">
                      <button
                        onClick={() => handleMissionConfigurartion()}
                        className="btn btn--outline fs-7 lh-1 ms-2"
                        disabled={missionContext.isMissionAborted}
                      >
                        {isDelegate ? (
                          <>
                            <i className="fa-solid fa-address-book"></i>
                            {t("-1", "Mission Contacts")}
                          </>
                        ) : (
                          <>
                            <i className="fa-solid fa-gear"></i>
                            {t("-1", "Mission Configuration")}
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </>
              )}
              {page === "configuration" && (
                <button
                  onClick={() => navigate(`/portal/mission-process/${id}/create`)}
                  className="btn btn--outline fs-7 lh-1 ms-2"
                >
                  <i className="fas fa-angle-left text-dark" />
                  {t("-1", "Back To Mission Info")}
                </button>
              )}
              {page === "changeform" && (
                <button
                  onClick={() =>
                    navigate(`/portal/mission-process/${id}/configuration`)
                  }
                  className="btn btn--outline fs-7 lh-1 ms-2"
                >
                  <i className="fas fa-angle-left text-dark" />
                  {t("-1", "Back To Mission Configuration")}
                </button>
              )}
              {page === "mission-logs" && (
                <button
                  onClick={() =>
                    navigate(`/portal/mission-process/${id}/create`)
                  }
                  className="btn btn--outline fs-7 lh-1 ms-2"
                >
                  <i className="fas fa-angle-left text-dark" />
                  {t("-1", "Back To Mission Info")}
                </button>
              )}
              {page === "create" && config.showCanAbortMission && missionContext.mission_status_id !== MISSION_STATUS_LIST.completed && !missionContext.is_archived && (
                <div className="ms-auto">
                  <button
                    onClick={() => {
                      setShowAbortMission(true);
                    }}
                    disabled={missionContext.isMissionAborted}
                    className="btn btn--outline fs-7 lh-1 ms-2"
                  >
                    {t("-1", "Abort Mission")}
                  </button>
                </div>
              )}
              {page === "create" && config.showCanArchiveMission && missionContext.mission_status_id === MISSION_STATUS_LIST.completed && (
                <div className="ms-auto">
                  <button
                    onClick={handleArchiveMission}
                    className="btn btn--outline fs-7 lh-1 ms-2"
                  >
                    {t("-1", "Archive Mission")}
                  </button>
                </div>
              )}
            </div>
          </div>
          <MissionInfo />
          {page === "create" && <MissionDashboard config={config} id={id} />}
          {page === "configuration" && <MissionConfigurationPage />}
          {page === "changeform" && <MissionChangeTabs />}
          {page === "mission-logs" && <EmailNotificationLogs />}
        </div>
      )}

      <MissionManagerModal
        isShow={isShow1}
        initModal={initModal1}
        data={userOptions}
      />
      <MissionAbortModal
        isShow={showAbortMission}
        initModal={() => setShowAbortMission((prev) => !prev)}
        reloadData={(reason) => abortMission(reason)}
      />

      <MissionLeaveModal
        isShow={showLeaveMission}
        initModal={() => setShowLeaveMission((prev) => !prev)}
        reloadData={() => { }}
        missionId={id}
      />
    </>
  );
};

MissionDetail.defaultProps = {
  config: {
    showAddMissionTasks: false,
    showCanAbortMission: false,
    showCanConfigureMission: false,
    showCanArchiveMission: false,
  },
};
export default MissionDetail;
